import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Trash2, Plus, Printer, FileImage, FileText, Loader2, Table2 } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { toolBtnClass } from "@/components/map/MapToolbar";
import { exportFeatures, fetchLayerFeatures } from "@/lib/exportData";
import { Input } from "@/components/ui/input";

export function SavedViews({ getState, onApply }) {
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/map/views").then(({ data }) => setViews(data)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const { data } = await api.post("/map/views", { name: name.trim(), ...getState() });
      setViews((v) => [data, ...v]); setName(""); toast.success(`View "${data.name}" saved`);
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (v) => {
    try { await api.delete(`/map/views/${v.id}`); setViews((vs) => vs.filter((x) => x.id !== v.id)); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="relative">
      <button type="button" data-testid="saved-views-toggle" title="Saved views" aria-label="Saved views" onClick={() => setOpen((o) => !o)} className={toolBtnClass(open)} style={open ? { background: "var(--terracotta)" } : {}}>
        <Bookmark size={17} />{views.length > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-900 dark:bg-emerald-500 text-white dark:text-slate-950 text-[9px] font-mono font-bold grid place-items-center">{views.length}</span>}
      </button>
      {open && (
        <div data-testid="saved-views-panel" className="absolute top-0 right-12 w-80 glass rounded-2xl p-4 shadow-2xl fade-up">
          <p className="data-label mb-3">Saved views</p>
          <form onSubmit={save} className="flex gap-2 mb-3">
            <Input data-testid="saved-view-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this view" className="h-9 rounded-full text-sm" />
            <button type="submit" data-testid="saved-view-save-button" className="h-9 w-9 shrink-0 rounded-full grid place-items-center bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950" aria-label="Save view"><Plus size={16} /></button>
          </form>
          {views.length === 0 && <p data-testid="saved-views-empty" className="text-xs text-muted-foreground">No saved views yet. Position the map, set layers and filters, then save.</p>}
          <ul className="space-y-1 max-h-60 overflow-auto">
            {views.map((v) => (
              <li key={v.id} data-testid={`saved-view-${v.id}`} className="flex items-center gap-2 text-sm rounded-xl px-2 py-1.5 hover:bg-muted/70">
                <button type="button" data-testid="saved-view-apply-button" onClick={() => { onApply(v); setOpen(false); }} className="flex-1 text-left truncate">
                  <span className="font-medium">{v.name}</span>
                  <span className="block text-[11px] text-muted-foreground font-mono">z{Math.round(v.zoom)} · {v.provider} · {formatDate(v.created_at)}</span>
                </button>
                <button type="button" data-testid="saved-view-delete-button" onClick={() => remove(v)} className="text-red-600 hover:bg-red-50 rounded-full p-1" aria-label="Delete view"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ExportMenu({ targetId, parcels, layers = [] }) {
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);

  const exportData = async (source, format) => {
    setOpen(false);
    try {
      const features = source === "parcels" ? parcels?.features || [] : await fetchLayerFeatures(source);
      if (!features.length) return toast.info("Nothing to export for that selection");
      exportFeatures(features, source === "parcels" ? "parcels" : source.name, format);
      toast.success(`${features.length} features exported as ${format.toUpperCase()}`);
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const vectorLayers = layers.filter((l) => l.kind === "vector");

  const capture = async () => {
    const { default: html2canvas } = await import("html2canvas");
    const el = document.getElementById(targetId);
    return html2canvas(el, { useCORS: true, allowTaint: false, logging: false, scale: 2, backgroundColor: "#0B0F17",
      ignoreElements: (node) => node.dataset?.noExport === "true" || node.classList?.contains("leaflet-control-zoom") });
  };
  const exportAs = async (kind) => {
    setBusy(kind); setOpen(false);
    try {
      const canvas = await capture();
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      if (kind === "png") {
        const a = document.createElement("a"); a.href = canvas.toDataURL("image/png"); a.download = `cgland-map-${stamp}.png`; a.click();
      } else {
        const { jsPDF } = await import("jspdf");
        const landscape = canvas.width >= canvas.height;
        const pdf = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "px", format: [canvas.width / 2, canvas.height / 2 + 40] });
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, canvas.width / 2, canvas.height / 2);
        pdf.setFontSize(9); pdf.setTextColor(90);
        pdf.text(`CG Land Geoportal · exported ${new Date().toLocaleString("en-IN")} · © OpenStreetMap contributors`, 12, canvas.height / 2 + 24);
        pdf.save(`cgland-map-${stamp}.pdf`);
      }
      toast.success(`Map exported as ${kind.toUpperCase()}`);
    } catch (err) { toast.error(`Export failed: ${err.message}`); } finally { setBusy(null); }
  };

  return (
    <div className="relative" data-no-export="true">
      <button type="button" data-testid="export-menu-toggle" title="Print / export" aria-label="Print / export" onClick={() => setOpen((o) => !o)} disabled={!!busy} className={`${toolBtnClass(open)} disabled:opacity-60`} style={open ? { background: "var(--terracotta)" } : {}}>
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Printer size={17} />}
      </button>
      {open && (
        <div data-testid="export-menu" className="absolute top-0 right-12 glass rounded-2xl p-2 shadow-2xl w-64 fade-up max-h-80 overflow-auto">
          <p className="px-3 pt-1 pb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Map image</p>
          <button type="button" data-testid="export-png-button" onClick={() => exportAs("png")} className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-xl hover:bg-muted"><FileImage size={14} /> Download PNG</button>
          <button type="button" data-testid="export-pdf-button" onClick={() => exportAs("pdf")} className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-xl hover:bg-muted"><FileText size={14} /> Download PDF</button>
          <p className="px-3 pt-3 pb-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Data (current filters)</p>
          <div className="flex items-center gap-1 px-3 py-1.5 text-sm"><Table2 size={14} className="shrink-0" /><span className="flex-1 truncate">Parcels ({parcels?.features?.length ?? 0})</span>
            <button type="button" data-testid="export-parcels-csv-button" onClick={() => exportData("parcels", "csv")} className="text-[11px] font-mono px-2 py-0.5 rounded-full border hover:bg-muted">CSV</button>
            <button type="button" data-testid="export-parcels-geojson-button" onClick={() => exportData("parcels", "geojson")} className="text-[11px] font-mono px-2 py-0.5 rounded-full border hover:bg-muted">GeoJSON</button></div>
          {vectorLayers.map((l) => (
            <div key={l.id} className="flex items-center gap-1 px-3 py-1.5 text-sm"><span className="w-3 h-3 rounded-sm shrink-0" style={{ background: l.color }} /><span className="flex-1 truncate" title={l.name}>{l.name}{l.filter?.value ? ` · ${l.filter.value}` : ""}</span>
              <button type="button" data-testid={`export-layer-csv-${l.id}`} onClick={() => exportData(l, "csv")} className="text-[11px] font-mono px-2 py-0.5 rounded-full border hover:bg-muted">CSV</button>
              <button type="button" data-testid={`export-layer-geojson-${l.id}`} onClick={() => exportData(l, "geojson")} className="text-[11px] font-mono px-2 py-0.5 rounded-full border hover:bg-muted">GeoJSON</button></div>
          ))}
        </div>
      )}
    </div>
  );
}
